// Fixtures of « Séance » (#/seance). The session REUSES the home PROGRAMME (Force & mobilité, Séance A · Bas du corps, 45 min,
// its 5 exercises in order). Prescriptions, rests, cues and coach advice are DEMO values, not a verified ACS programme. Previous
// loads are DERIVED from the last logged Séance A in Mon suivi (suivi-fixtures), not duplicated. Photos are authorised ACS
// atmosphere photos: they never claim to demonstrate the exact movement. No exercise video exists: the slot says so.
import { PROGRAMME } from "./home-fixtures";
import { PHOTOS } from "./photos";
import { SESSIONS } from "./suivi-fixtures";
import { TEAM } from "./studio-fixtures";

export interface SeanceExercise {
  name: string; sets: number; reps: number; unit: "reps" | "s"; rest: string; loaded: boolean; group?: string;
  cues: string[]; photo: string; previousLoad?: number;
}

const lastA = SESSIONS.filter(s => s.title.startsWith("Séance A")).sort((a, b) => b.date.localeCompare(a.date))[0];
const prevLoad = (name: string) => lastA?.exercises?.find(e => name.toLowerCase().startsWith(e.name.toLowerCase()))?.load;

/** Demo prescription per exercise name of the programme; group « Superset » pairs exercises 3 and 4. */
const RX: Record<string, Omit<SeanceExercise, "name" | "previousLoad">> = {
  "Squat gobelet": { sets: 3, reps: 8, unit: "reps", rest: "90 s", loaded: true, photo: PHOTOS.exercise,
    cues: ["Pieds largeur d'épaules, pointes légèrement ouvertes.", "Descends buste haut, genoux dans l'axe des pieds.", "Remonte en poussant le sol, expire en haut."] },
  "Soulevé de terre roumain": { sets: 3, reps: 8, unit: "reps", rest: "90 s", loaded: true, photo: PHOTOS.deadlift,
    cues: ["Haltères contre les cuisses, genoux souples.", "Pousse les hanches vers l'arrière, dos neutre.", "Remonte en serrant les fessiers."] },
  "Fentes arrière alternées": { sets: 3, reps: 8, unit: "reps", rest: "60 s après le superset", loaded: true, group: "Superset", photo: PHOTOS.second,
    cues: ["Grand pas en arrière, buste droit.", "Le genou arrière frôle le sol.", "Reviens en poussant sur le talon avant."] },
  "Hip thrust au banc": { sets: 3, reps: 12, unit: "reps", rest: "60 s après le superset", loaded: true, group: "Superset", photo: PHOTOS.featured,
    cues: ["Omoplates sur le banc, pieds à plat.", "Monte jusqu'à l'alignement épaules, hanches, genoux.", "Marque une seconde en haut."] },
  "Gainage planche": { sets: 3, reps: 40, unit: "s", rest: "45 s", loaded: false, photo: PHOTOS.upNext,
    cues: ["Coudes sous les épaules.", "Bassin aligné, ni creusé ni relevé.", "Respire calmement pendant le maintien."] },
};

export const SEANCE = PROGRAMME ? {
  id: "s3-A", programme: PROGRAMME.title, title: PROGRAMME.session, minutes: PROGRAMME.minutes, photo: PROGRAMME.photo,
  exercises: PROGRAMME.exercises.map((name): SeanceExercise => ({ name, ...(RX[name] ?? { sets: 3, reps: 10, unit: "reps", rest: "60 s", loaded: false, photo: PHOTOS.upNext, cues: [] }), previousLoad: prevLoad(name) })),
} : null;
export type SessionDef = NonNullable<typeof SEANCE>;

/** Same construction as SEANCE for another session of a weekly programmation (demo prescriptions: RX when known, else the same
 *  neutral default). Exercises unknown → empty list (the page says so). */
export const buildSession = (o: { id: string; programme: string; title: string; minutes: number; exercises?: readonly string[]; photo?: string }): SessionDef => ({
  id: o.id, programme: o.programme, title: o.title, minutes: o.minutes, photo: o.photo ?? PROGRAMME?.photo ?? PHOTOS.upNext,
  exercises: (o.exercises ?? []).map((name): SeanceExercise => ({ name, ...(RX[name] ?? { sets: 3, reps: 10, unit: "reps", rest: "60 s", loaded: false, photo: PHOTOS.upNext, cues: [] }), previousLoad: prevLoad(name) })),
});

/** Coach of the session: real name and official portrait (studio-fixtures); the assignment and the advice text are DEMO. */
export const COACH = TEAM[0] ? { name: TEAM[0].firstName, photo: TEAM[0].photo, alt: TEAM[0].alt } : null;
export const COACH_ADVICE = {
  preview: "Commence léger sur la première série, puis ajuste la charge pour garder 1 à 2 répétitions en réserve.",
  full: [
    "Échauffe-toi 5 minutes avant la première série.",
    "Commence léger sur la première série, puis ajuste la charge pour garder 1 à 2 répétitions en réserve.",
    "Sur le superset, enchaîne les fentes et le hip thrust, puis récupère.",
    "Note ton ressenti à la fin : il servira à ajuster la prochaine séance.",
  ],
};
